
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from "@/hooks/use-toast";
import { Shield, Menu, X } from 'lucide-react';
import Button from '@/components/ui-elements/button';
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';
import AdminHeaderMobileMenu from './AdminHeaderMobileMenu';
import AdminHeaderDesktopMenu from './AdminHeaderDesktopMenu';
import AdminMakeAdminDialog from './AdminMakeAdminDialog';
import { useAdminContext } from '@/context/AdminContext';

const AdminHeader: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation(['admin', 'dashboard', 'common']);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const isMobile = useIsMobile();
  const { refreshAdminStatus } = useAdminContext();

  const handleLogout = useCallback(async () => {
    try {
      // Clear admin cache to ensure proper state on next login
      localStorage.removeItem('adminCheckPerformed');
      localStorage.removeItem('adminCheckTimestamp');
      await supabase.auth.signOut();
      navigate('/');
      toast({
        title: t('dashboard:loggedOut'),
        description: t('dashboard:loggedOutSuccess')
      });
    } catch (error) {
      console.error('Error logging out:', error);
    }
  }, [navigate, toast, t]);

  const handleGoToDashboard = useCallback(() => {
    navigate('/dashboard');
  }, [navigate]);

  const handleOpenDialog = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  const toggleMenu = useCallback(() => {
    setIsMenuOpen(prev => !prev);
  }, []);

  const handleOpenAppSettings = useCallback(() => {
    navigate('/admin/settings');
  }, [navigate]);

  const handleOpenEventsManagement = useCallback(() => {
    navigate('/admin/events');
  }, [navigate]);

  const handleOpenBadgesManagement = useCallback(() => {
    navigate('/admin/badges');
  }, [navigate]);

  const handleMakeAdmin = async () => {
    if (!email) {
      toast({
        title: t('admin:error'),
        description: t('admin:pleaseEnterEmail'),
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      // First, check if the user exists
      const { data: userData, error: userError } = await supabase
        .from('customer_profiles')
        .select('id, email, tier')
        .eq('email', email.trim())
        .single();

      if (userError) {
        throw new Error(t('admin:userNotFound'));
      }

      // Update the user's tier to admin
      const { error: updateError } = await supabase
        .from('customer_profiles')
        .update({ tier: 'admin' })
        .eq('id', userData.id);

      if (updateError) throw updateError;

      toast({
        title: t('admin:success'),
        description: t('admin:userPromotedToAdmin', { email: email })
      });
      setIsDialogOpen(false);
      setEmail('');
      
      // Force refresh admin status in case the current user was promoted
      refreshAdminStatus();
    } catch (error: any) {
      toast({
        title: t('admin:error'),
        description: error.message || t('admin:errorUpdatingTier'),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <header className="bg-[#1A1F2C] text-white sticky top-0 z-10 shadow-md">
      <div className="container mx-auto flex justify-between items-center py-4 px-4">
        <div className="flex items-center gap-3">
          <Shield className="h-8 w-8 text-red-500" aria-hidden="true" />
          <h1 className="text-lg font-bold truncate max-w-[150px] sm:max-w-none sm:text-xl">
            {t('admin:adminPanel')}
          </h1>
        </div>

        {isMobile ? (
          <>
            <Button 
              variant="outline" 
              size="sm" 
              className="text-white border-white hover:bg-white/20" 
              onClick={toggleMenu}
              aria-expanded={isMenuOpen ? "true" : "false"}
              aria-controls="mobile-menu"
              aria-label={isMenuOpen ? t('common:closeMenu') : t('common:openMenu')}
            >
              {isMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
            </Button>
            
            <AdminHeaderMobileMenu 
              isOpen={isMenuOpen} 
              handleGoToDashboard={handleGoToDashboard} 
              handleOpenDialog={handleOpenDialog} 
              handleOpenAppSettings={handleOpenAppSettings} 
              handleOpenEventsManagement={handleOpenEventsManagement} 
              handleOpenBadgesManagement={handleOpenBadgesManagement} 
              handleLogout={handleLogout} 
            />
          </>
        ) : (
          <AdminHeaderDesktopMenu 
            handleGoToDashboard={handleGoToDashboard} 
            handleOpenDialog={handleOpenDialog} 
            handleOpenAppSettings={handleOpenAppSettings} 
            handleOpenEventsManagement={handleOpenEventsManagement} 
            handleOpenBadgesManagement={handleOpenBadgesManagement} 
            handleLogout={handleLogout} 
          />
        )}
      </div>

      <AdminMakeAdminDialog 
        isOpen={isDialogOpen} 
        setIsOpen={setIsDialogOpen} 
        email={email} 
        setEmail={setEmail} 
        handleMakeAdmin={handleMakeAdmin} 
        loading={loading} 
      />
    </header>
  );
};

export default AdminHeader;
